import React, { useEffect, useRef, useState } from "react";
import FunnelChart from "./FunnelChart";
import FunnelTable from "./FunnelTable";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig } from "./PanelHeadline";
import { fetchFunnelAnalytics, fetchAvailableCities } from "../services/analyticsService";
import axiosInstance from "../api/axiosInstance";

const FunnelAnalyticsPage = ({ startLoading, stopLoading }) => {
    const [data, setData] = useState([]);
    // const [loading, setLoading] = useState(false);
    const isFirstLoad = useRef(true);
    const [cityOptions, setCityOptions] = useState([]);
    // Pocket filter (2026-08-28): a launch city is a POCKET, not an address
    // string. Ottawa spans 29 municipalities and its members carry several
    // different address spellings, so the city list can never select it.
    const [pocketOptions, setPocketOptions] = useState([]);



    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
        platform: "all",
        city: "",
        gender: "all",
        orientation: "all",
        ageGroupMin: "",
        ageGroupMax: "",
        source: "all",
        // The Launch Pockets select reads filters.pocketId, so it has to exist
        // here or the select mounts uncontrolled and React warns the first
        // time it changes.
        pocketId: "",
    });

    // Orientation values as stored on user profiles
    const orientationOptions = [
        "straight",
        "gay",
        "lesbian",
        "bisexual",
        "asexual",
        "sexually_fluid",
    ]

    const orientationLabel = (opt) =>
        opt
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");

    useEffect(() => {
        loadFunnelData();
        loadCities()
    }, []);

    const loadFunnelData = async () => {
        try {
            if (isFirstLoad.current) startLoading();

            const params = {
                fromDate: filters.startDate || undefined,
                toDate: filters.endDate || undefined,
                city: filters.city ? filters.city : undefined,
                pocketId: filters.pocketId || undefined,
                gender: filters.gender !== "all" ? filters.gender : undefined,
                orientation:
                    filters.orientation !== "all" ? filters.orientation : undefined,
                platform: filters.platform !== "all" ? filters.platform : undefined,
                minAge: filters.ageGroupMin || undefined,
                maxAge: filters.ageGroupMax || undefined,
                acquisitionSource: filters.source !== "all" ? filters.source : undefined,
            };

            const res = await fetchFunnelAnalytics(params);
            const body = res?.data;
            const rowsByStep = new Map(
                (Array.isArray(body?.data) ? body.data : []).map((r) => [r.step, r])
            );
            const stepOrder = Array.isArray(body?.steps) ? body.steps : [];

            const merged =
                stepOrder.length > 0
                    ? stepOrder.map(({ step, label }) => {
                          const row = rowsByStep.get(step);
                          return row
                              ? { ...row, label: label ?? row.step?.replace(/_/g, " ") }
                              : {
                                    step,
                                    users: 0,
                                    conversion: 0,
                                    label: label ?? step?.replace(/_/g, " "),
                                };
                      })
                    : (body?.data || []).map((row) => ({
                          ...row,
                          label:
                              row.label ??
                              row.step?.replace(/_/g, " ") ??
                              "",
                      }));

            setData(merged);
        } catch (err) {
            console.error("Failed to load funnel analytics", err);
        } finally {
            if (isFirstLoad.current) {
                stopLoading();
                isFirstLoad.current = false;
            }
        }
    };

    const loadCities = async () => {
        try {
            const res = await fetchAvailableCities();
            if (res.data?.status) {
                setCityOptions(res.data.data || []);
            }
            try {
                const pk = await axiosInstance.get("/admin/pockets/stats");
                setPocketOptions((pk.data?.pockets || []).filter((p) => !p.isReviewPocket));
            } catch (e) {
                console.error("Failed to fetch pockets", e);
            }
        } catch (err) {
            console.error("Failed to fetch cities", err);
        }
    };


    return (
        <AnalyticsSection
            id="signup"
            icon="mdi:filter-outline"
            title="Member funnel"
            basis="Members here today, Photos, Face verification, Gatekeeper, Compatibility assigned, Vault started, Vault completed, Reveal, Chat, Purchase. Row 1 is every member who exists today, not sign-ups: at least 493 accounts have ever existed. All time and filterable, so it never reconciles with the weekly member loop at the top of this page. Photos and face verification are steps 2 and 3, which is where members actually stop. Purchase counts every buyer, not only those who chatted."
        >
            <>
                {(() => {
                    // The largest ABSOLUTE drop between two consecutive steps.
                    // Absolute, never percent: on a pocket this size a step that
                    // halves from 4 to 2 is a bigger percentage than one that
                    // loses 89 people, and only one of those is worth acting on.
                    let worst = null;
                    for (let i = 1; i < data.length; i++) {
                        // A row with a `basis` (Purchase) is not a subset of the row
                        // above it, so "chat to purchase" is not a drop anyone made.
                        if (data[i].basis) continue;
                        const lost = (data[i - 1].users || 0) - (data[i].users || 0);
                        if (lost > 0 && (!worst || lost > worst.lost)) {
                            worst = { lost, from: data[i - 1].label, to: data[i].label };
                        }
                    }
                    const start = data[0]?.users || 0;
                    return (
                        <PanelHeadline
                            empty={!data.length || !start}
                            emptyText="No members match these filters."
                        >
                            Of <Fig>{start.toLocaleString()}</Fig> members here today,{" "}
                            <Fig>{data[data.length - 1]?.users ?? 0}</Fig> reach{" "}
                            {data[data.length - 1]?.label ?? "the end"}.
                            {worst && (
                                <>
                                    {" "}The largest single drop is{" "}
                                    <Fig>
                                        {worst.from} to {worst.to}
                                    </Fig>
                                    , <Fig>{worst.lost}</Fig>{" "}
                                    {worst.lost === 1 ? "person" : "people"}.
                                </>
                            )}
                        </PanelHeadline>
                    );
                })()}
                {/* 🔍 Filters */}

                {/* Row 1 */}
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <input
                            type="date"
                            className="form-control"
                            value={filters.startDate}
                            onChange={(e) =>
                                setFilters({ ...filters, startDate: e.target.value })
                            }
                        />
                    </div>

                    <div className="col-md-3">
                        <input
                            type="date"
                            className="form-control"
                            value={filters.endDate}
                            onChange={(e) =>
                                setFilters({ ...filters, endDate: e.target.value })
                            }
                        />
                    </div>

                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={filters.pocketId}
                            onChange={(e) =>
                                setFilters({ ...filters, pocketId: e.target.value })
                            }
                        >
                            <option value="">All Launch Pockets</option>
                            {pocketOptions.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}{p.status ? " (" + p.status + ")" : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                      
                        <select
                            className="form-select"
                            value={filters.city}
                            onChange={(e) =>
                                setFilters({ ...filters, city: e.target.value })
                            }
                        >
                            <option value="">All Cities</option>
                            {cityOptions.map((city) => (
                                <option key={city} value={city}>
                                    {city}
                                </option>
                            ))}
                        </select>
                    </div>





                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={filters.gender}
                            onChange={(e) =>
                                setFilters({ ...filters, gender: e.target.value })
                            }
                        >
                            <option value="all">All Genders</option>
                            <option value="man">Man</option>
                            <option value="woman">Woman</option>
                            <option value="beyond_binary">Beyond Binary</option>
                        </select>
                    </div>
                </div>

                {/* Row 2 */}
                <div className="row g-2 align-items-end mb-4">
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={filters.orientation}
                            onChange={(e) =>
                                setFilters({ ...filters, orientation: e.target.value })
                            }
                        >
                            <option value="all">All Orientations</option>
                            {orientationOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                    {orientationLabel(opt)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-3">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="Min Age"
                            value={filters.ageGroupMin}
                            onChange={(e) =>
                                setFilters({ ...filters, ageGroupMin: e.target.value })
                            }
                        />
                    </div>

                    <div className="col-md-3">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="Max Age"
                            value={filters.ageGroupMax}
                            onChange={(e) =>
                                setFilters({ ...filters, ageGroupMax: e.target.value })
                            }
                        />
                    </div>

                    {/* Apply Button */}
                    <div className="col-md-3 ms-auto">
                        <button
                            className="btn btn-primary w-100"
                            onClick={loadFunnelData}
                        >
                            Apply
                        </button>
                    </div>
                </div>


                {/* 📊 Funnel */}

                <>
                    <FunnelChart data={data} />
                    <FunnelTable data={data} />
                </>

            </>
        </AnalyticsSection>
    );
};

export default FunnelAnalyticsPage;
